// Customer-facing presentation for ATLAS intelligence labels.
// Keeps underlying backend values unchanged; only cleans the DISPLAY so
// engineering/internal wording (VERY_HIGH, PENDING, snake_case step codes,
// "Production", "adaptive performance", …) never reaches the customer.
// Never asserts an outcome the source data does not support — these map the
// label/stage name only; success/availability is still driven by real fields.

type Lang = "tr" | "en";

const GRADE_TR: Record<string, string> = {
  VERY_HIGH: "Çok Yüksek", HIGH: "Yüksek", MEDIUM: "Orta", MODERATE: "Orta",
  LOW: "Düşük", VERY_LOW: "Çok Düşük", PENDING: "Bekliyor", SUCCESS: "Tamamlandı",
  COMPLETED: "Tamamlandı", FAILED: "Başarısız", UNKNOWN: "—", STRONG: "Güçlü",
  WEAK: "Zayıf", NONE: "Yok", GOOD: "İyi", EXCELLENT: "Mükemmel", AVERAGE: "Orta",
};
const GRADE_EN: Record<string, string> = {
  VERY_HIGH: "Very High", HIGH: "High", MEDIUM: "Medium", MODERATE: "Moderate",
  LOW: "Low", VERY_LOW: "Very Low", PENDING: "Pending", SUCCESS: "Complete",
  COMPLETED: "Complete", FAILED: "Failed", UNKNOWN: "—", STRONG: "Strong",
  WEAK: "Weak", NONE: "None", GOOD: "Good", EXCELLENT: "Excellent", AVERAGE: "Average",
};

function titleCase(v: string): string {
  return v.replace(/[_\s]+/g, " ").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

// Map an enum-like grade/status token to clean localized text. Unknown values
// are de-jargoned (underscores removed, Title Case) rather than shown raw.
export function localizeGrade(v: string | null | undefined, lang: Lang): string | null {
  if (v == null || v === "") return null;
  const key = v.toString().trim().toUpperCase().replace(/[\s-]+/g, "_");
  const m = (lang === "tr" ? GRADE_TR : GRADE_EN)[key];
  if (m) return m;
  return titleCase(v.toString());
}

// Decision-replay step → concise product language. Matches on code/title
// keywords; unknown steps fall back to a de-jargoned title. Describes the STAGE
// only (not the outcome — the UI still shows real status/availability).
export function localizeStepTitle(step: { code?: string | null; title?: string | null }, lang: Lang): string {
  const hay = `${step.code || ""} ${step.title || ""}`.toLowerCase();
  const rules: [RegExp, string, string][] = [
    [/learn/, "Öğrenme durumu güncellendi", "Learning state updated"],
    [/pattern|örüntü/, "Örüntü analizi tamamlandı", "Pattern analysis completed"],
    [/perf|adaptive|live/, "Canlı performans değerlendirildi", "Live performance evaluated"],
    [/final|produc|output/, "Nihai karar üretildi", "Final decision produced"],
    [/factor|feature|signal/, "Karar faktörleri oluşturuldu", "Decision factors assembled"],
    [/data|load|input|fetch|collect|ingest/, "Karar verisi yüklendi", "Decision data loaded"],
  ];
  for (const [re, tr, en] of rules) if (re.test(hay)) return lang === "tr" ? tr : en;
  const base = (step.title || step.code || "").toString().trim();
  return base ? titleCase(base) : (lang === "tr" ? "Karar adımı" : "Decision step");
}
